import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { TourState, TourDefinition, TourId, TourStep } from "./types";

/**
 * Resolve a step's target route, awaiting an async `resolveRoute` if present
 * and falling back to the static `route` on error / empty result.
 *
 * Short-circuits when the current location already satisfies the static
 * `route`: many consecutive tour steps share a page (e.g. 4 invoice-detail
 * steps), so we avoid both an extra API call and a no-op history.push.
 */
async function resolveStepRoute(
  step: TourStep,
  currentLoc?: { pathname: string; search: string }
): Promise<string> {
  if (currentLoc && routeMatches(currentLoc, step.route) && !step.resolveRoute) {
    return step.route;
  }
  if (step.resolveRoute) {
    // If we're already on a URL that satisfies the static fallback (e.g.
    // invoice detail with some invoiceId), keep the user on it instead of
    // forcing a re-navigation.
    if (currentLoc && routeMatches(currentLoc, step.route)) {
      return `${currentLoc.pathname}${currentLoc.search}`;
    }
    try {
      const dynamic = await step.resolveRoute();
      if (dynamic) return dynamic;
    } catch (err) {
      // Swallow — fall through to static route below.
      // eslint-disable-next-line no-console
      console.warn("[tour] resolveRoute failed, using static route", err);
    }
  }
  return step.route;
}

const STORAGE_KEY = "docuclipper.tour";

function splitRoute(route: string): { pathname: string; search: string } {
  const qIdx = route.indexOf("?");
  if (qIdx === -1) return { pathname: route, search: "" };
  return { pathname: route.slice(0, qIdx), search: route.slice(qIdx) };
}

function routeMatches(loc: { pathname: string; search: string }, route: string): boolean {
  const target = splitRoute(route);
  if (loc.pathname !== target.pathname) return false;
  if (!target.search) return true;
  const have = new URLSearchParams(loc.search);
  const want = new URLSearchParams(target.search);
  for (const [k, v] of want.entries()) {
    if (have.get(k) !== v) return false;
  }
  return true;
}

function routeDiffers(loc: { pathname: string; search: string }, route: string): boolean {
  return !routeMatches(loc, route);
}

function loadPersistedState(): TourState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveState(state: TourState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function clearState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

interface TourContextValue {
  state: TourState;
  tours: Record<TourId, TourDefinition>;
  startTour: (tourId: TourId) => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (index: number) => void;
  skipTour: () => void;
  registerTour: (def: TourDefinition) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside TourProvider");
  return ctx;
};

export const TourProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const [tours, setTours] = useState<Record<TourId, TourDefinition>>({});
  const [state, setState] = useState<TourState>(() => {
    const persisted = loadPersistedState();
    return persisted || { tourId: null, stepIndex: 0, active: false };
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const persist = useCallback((next: TourState) => {
    setState(next);
    if (next.active) {
      saveState(next);
    } else {
      clearState();
    }
  }, []);

  const registerTour = useCallback((def: TourDefinition) => {
    setTours(prev => ({ ...prev, [def.id]: def }));
  }, []);

  const startTour = useCallback((tourId: TourId) => {
    const next: TourState = { tourId, stepIndex: 0, active: true };
    persist(next);

    // Navigate to the tour's first step so the overlay can anchor.
    // If l1/l2 query params aren't present in the current URL but the tour
    // targets a specific project, the defaults in step.route are used as-is.
    const def = tours[tourId];
    const firstStep = def?.steps?.[0];
    if (firstStep) {
      resolveStepRoute(firstStep, location).then((r) => {
        if (r && routeDiffers(location, r)) history.push(r);
      });
    }
  }, [persist, tours, location.pathname, location.search, history]);

  const skipTour = useCallback(() => {
    persist({ tourId: null, stepIndex: 0, active: false });
  }, [persist]);

  const nextStep = useCallback(() => {
    const { tourId, stepIndex } = stateRef.current;
    if (!tourId) return;

    const toursSnap = tours;
    const def = toursSnap[tourId];
    if (!def) return;

    const next = stepIndex + 1;
    if (next >= def.steps.length) {
      persist({ tourId: null, stepIndex: 0, active: false });
      return;
    }

    const nextStepDef = def.steps[next];
    const nextState: TourState = { tourId, stepIndex: next, active: true };
    persist(nextState);

    resolveStepRoute(nextStepDef, location).then((r) => {
      if (r && routeDiffers(location, r)) history.push(r);
    });
  }, [tours, location.pathname, location.search, history, persist]);

  const goToStep = useCallback((index: number) => {
    const { tourId } = stateRef.current;
    if (!tourId) return;
    const def = tours[tourId];
    if (!def) return;
    if (index < 0 || index >= def.steps.length) return;

    const stepDef = def.steps[index];
    persist({ tourId, stepIndex: index, active: true });

    resolveStepRoute(stepDef, location).then((r) => {
      if (r && routeDiffers(location, r)) history.push(r);
    });
  }, [tours, location.pathname, location.search, history, persist]);

  const previousStep = useCallback(() => {
    const { stepIndex } = stateRef.current;
    goToStep(stepIndex - 1);
  }, [goToStep]);

  // When route changes, check if the active step matches the new route.
  // If not, look ahead to find a step on this route.
  useEffect(() => {
    const { tourId, stepIndex, active } = stateRef.current;
    if (!active || !tourId) return;
    const def = tours[tourId];
    if (!def) return;

    const currentStep = def.steps[stepIndex];
    if (!currentStep || routeMatches(location, currentStep.route)) return;

    // Find the next step that matches this route (user may have navigated naturally)
    for (let i = stepIndex + 1; i < def.steps.length; i++) {
      if (routeMatches(location, def.steps[i].route)) {
        const next: TourState = { tourId, stepIndex: i, active: true };
        persist(next);
        return;
      }
    }
  }, [location.pathname, location.search, tours]);

  return (
    <TourContext.Provider value={{ state, tours, startTour, nextStep, previousStep, goToStep, skipTour, registerTour }}>
      {children}
    </TourContext.Provider>
  );
};
